import type { Dayjs } from 'dayjs';
import { useState } from 'react';

import { TimePicker } from '@/components/common/date-picker';
import { Switch } from '@/components/common/switch';
import { Drawer } from '@/components/feedback/drawer';

import styles from './schedule-drawer.module.scss';

const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export type ScheduleDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** Confirm the schedule; the parent issues the command + shows the lifecycle. */
  onConfirm: () => void;
};

/**
 * Schedule Irrigation drawer (story 104144 / Figma 2322-693401 & -695261). Start/Stop
 * time, Sun–Sat day pills, Repeat Every Week. At least one day is required. When days
 * are selected the footer offers "Reset Schedule" (clears the selection) alongside Save.
 */
export function ScheduleDrawer({ open, onClose, onConfirm }: ScheduleDrawerProps) {
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const [days, setDays] = useState<number[]>([]);
  const [repeat, setRepeat] = useState(true);

  const hasDays = days.length > 0;
  const valid = !!start && !!end && hasDays;

  const toggleDay = (value: number) =>
    setDays((d) => (d.includes(value) ? d.filter((x) => x !== value) : [...d, value]));

  const reset = () => {
    setStart(null);
    setEnd(null);
    setDays([]);
    setRepeat(true);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Schedule Irrigation"
      primaryText="Save"
      primaryVariant="neutral"
      onPrimary={valid ? onConfirm : undefined}
      cancelText={hasDays ? 'Reset Schedule' : 'Cancel'}
      onCancel={hasDays ? reset : onClose}
    >
      <div className={styles.body}>
        <div className={styles.times}>
          <div className={styles.field}>
            <span className={styles.label}>Start Time</span>
            <TimePicker
              value={start}
              onChange={(v) => setStart(v)}
              use12Hours
              format="h:mm A"
              placeholder="Choose"
            />
          </div>
          <div className={styles.field}>
            <span className={styles.label}>End Time</span>
            <TimePicker
              value={end}
              onChange={(v) => setEnd(v)}
              use12Hours
              format="h:mm A"
              placeholder="Choose"
            />
          </div>
        </div>

        <div className={styles.days}>
          {DAYS.map((d) => {
            const active = days.includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                aria-pressed={active}
                aria-label={d.label}
                className={[styles.day, active ? styles.active : undefined]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => toggleDay(d.value)}
              >
                {d.label}
              </button>
            );
          })}
        </div>

        <div className={styles.repeat}>
          <span className={styles.repeatLabel}>Repeat Every Week</span>
          <Switch checked={repeat} onChange={setRepeat} />
        </div>

        {!hasDays ? <span className={styles.hint}>Select at least one day.</span> : null}
      </div>
    </Drawer>
  );
}
